// Pure, framework-free helpers for reading a CandidateSyncConfig out of a Company's `atsIntegration`
// blob and writing an edited one back — the candidate counterpart of ../job-sync/jobSyncConfig.js.
// The config is a SIBLING of jobSyncConfig on the same entry; no schema change, `atsIntegration`
// stays an AWSJSON string on the wire.
//
// Extraction is STRICTER than job-sync, mirroring the engine
// (process-candidates/src/services/candidateConfig.ts): this pipeline can trigger bonus payouts and
// writes into the client's production ATS, so when more than one entry carries a config the editor
// must refuse to guess (the engine throws), and the host entry is addressed by array INDEX — the
// engine takes the single carrier wherever it sits; ATSName is a label here, not the key.
//
// validateCandidateConfig mirrors the engine's validateCandidateSyncConfig rule-for-rule; additions
// there must gain a check (and a test) here so a saved config can never break the Lambda.

function parseMaybe(value) {
	if (typeof value !== 'string') return value;
	try {
		return JSON.parse(value);
	} catch {
		return value;
	}
}

// Structural deep-clone. Configs are pure JSON, so the round-trip is exact — and it strips any
// accidental `undefined`, which must never reach the emitted JSON.
export function cloneConfig(config) {
	return JSON.parse(JSON.stringify(config));
}

export function isDirty(config, initialJson) {
	return JSON.stringify(config) !== initialJson;
}

// The engine's default admin note for the ineligible-prior-application rule — Workato's note text
// verbatim (process-candidates/src/services/candidateStatus.ts DEFAULT_INELIGIBLE_NOTE). Shown as
// the placeholder while the config leaves `adminNoteTemplate` unset, so operators see exactly what
// the run will write. "{applyDate}" is replaced with the application date (YYYY-MM-DD).
export const DEFAULT_INELIGIBLE_NOTE =
	'This candidate already had an active application in Greenhouse (applied {applyDate}) before the referral was submitted, so the referral is not eligible for a bonus.';

// List the ATS entries that could host a candidate config, keyed by ORIGINAL array index so a write
// lands on exactly the entry that was read, even with duplicate ATSNames.
export function listAtsEntries(atsIntegration) {
	const arr = parseMaybe(atsIntegration);
	if (!Array.isArray(arr)) return [];
	const out = [];
	arr.forEach((e, index) => {
		if (!e || !e.ATSName) return;
		out.push({
			atsName: e.ATSName,
			index,
			hasCandidateConfig: !!e.candidateSyncConfig,
			hasJobSyncConfig: !!e.jobSyncConfig,
		});
	});
	return out;
}

// Every entry carrying a candidateSyncConfig. One is the normal case; zero means the create flow;
// more than one blocks editing entirely (mirror of the engine's refusing-to-guess throw).
export function candidateCarriers(atsIntegration) {
	const arr = parseMaybe(atsIntegration);
	if (!Array.isArray(arr)) return [];
	const out = [];
	arr.forEach((e, index) => {
		if (e && typeof e === 'object' && e.candidateSyncConfig) {
			out.push({ atsName: e.ATSName || '?', index });
		}
	});
	return out;
}

// Read the config on the entry at `index`. Defensive like the job-sync reader: the blob may arrive
// parsed or as an AWSJSON string, and the nested config may itself be a stringified object.
export function readCandidateConfig(atsIntegration, index) {
	const arr = parseMaybe(atsIntegration);
	if (!Array.isArray(arr)) return null;
	const entry = arr[index];
	if (!entry || entry.candidateSyncConfig == null) return null;
	const cfg = parseMaybe(entry.candidateSyncConfig);
	return cfg && typeof cfg === 'object' ? cfg : null;
}

// Patch ONLY the addressed entry's candidateSyncConfig on a FRESH array, preserving every other
// entry and every other key of the host entry (credentials, jobSyncConfig, …) verbatim — the spread
// keeps the entry's key order, so an untouched load→save round-trip re-stringifies byte-identically.
// Never mutates the input. The caller JSON.stringifies the result for updateATSIntegration.
export function writeCandidateConfig(atsIntegration, index, config) {
	const arr = parseMaybe(atsIntegration);
	if (!Array.isArray(arr)) throw new Error('atsIntegration is not an array');
	const entry = arr[index];
	if (!entry || typeof entry !== 'object') {
		throw new Error(`No atsIntegration entry at index ${index}. Reload the page and try again.`);
	}
	return arr.map((e, i) => (i === index ? { ...e, candidateSyncConfig: cloneConfig(config) } : e));
}

// A minimal VALID config for the create flow, in the safe state everywhere: dry-run, write-back and
// job-create off, Workato's pull/rule defaults (accepted/interviewing/notHired, fallback
// interviewing), and the shipping circuit-breaker caps. Convention preserved: organizationId ==
// companyId; erinConnect.type == the host entry's ATSName.
export function scaffoldCandidateConfig(entry, companyId) {
	const atsName = entry && entry.ATSName ? entry.ATSName : '';
	return {
		version: 1,
		companyId,
		organizationId: companyId,
		erinConnect: { type: atsName },
		mode: 'dry-run',
		identity: {},
		pull: { referralStatuses: ['accepted', 'interviewing', 'notHired'] },
		rules: {
			ineligiblePriorApplication: { enabled: false, alreadySourcedIds: [] },
			fallbackStatus: 'interviewing',
		},
		greenhouseWriteback: { enabled: false },
		jobCreate: { enabled: false, defaults: {} },
		limits: { maxActions: 50, maxHiredWrites: 3 },
	};
}

// Mirror of the engine's validateCandidateSyncConfig, one entry per rule, with UI paths so each
// error can render inline at its field as well as in the save bar.
export function validateCandidateConfig(config) {
	const errors = [];
	const push = (path, message) => errors.push({ path, message });
	if (!config || typeof config !== 'object') {
		return [{ path: '', message: 'Config is missing.' }];
	}

	if (config.version !== 1) push('version', 'Config version must be 1.');
	if (!isNonEmptyString(config.companyId)) push('companyId', 'companyId is required.');
	if (config.organizationId !== undefined && config.organizationId !== config.companyId) {
		push('organizationId', 'organizationId must equal companyId (claraty org == ERIN company by convention).');
	}
	if (!config.erinConnect || !isNonEmptyString(config.erinConnect.type)) {
		push('erinConnect.type', "Integration type (the host entry's ATSName) is required.");
	}
	if (config.mode !== 'dry-run' && config.mode !== 'live') push('mode', 'Mode must be Dry-run or Live.');

	const statuses = config.pull && config.pull.referralStatuses;
	if (!Array.isArray(statuses) || statuses.length === 0 || !statuses.every(isNonEmptyString)) {
		push('pull.referralStatuses', 'At least one ERIN referral status is required for the match pool.');
	}

	if (!config.rules || !isNonEmptyString(config.rules.fallbackStatus)) {
		push('rules.fallbackStatus', 'A fallback status is required — it is used when status mappings produce nothing.');
	}
	const ineligible = config.rules && config.rules.ineligiblePriorApplication;
	if (ineligible && ineligible.enabled && !Array.isArray(ineligible.alreadySourcedIds)) {
		push(
			'rules.ineligiblePriorApplication.alreadySourcedIds',
			'Already-sourced ids must be a list while the rule is enabled.'
		);
	}

	const equivalences = config.identity && config.identity.emailDomainEquivalences;
	if (equivalences !== undefined) {
		const ok =
			Array.isArray(equivalences) &&
			equivalences.every((g) => Array.isArray(g) && g.length >= 2 && g.every(isNonEmptyString));
		if (!ok) push('identity.emailDomainEquivalences', 'Each domain group needs at least two non-empty domains.');
	}

	const writeback = config.greenhouseWriteback;
	if (!writeback || typeof writeback.enabled !== 'boolean') {
		push('greenhouseWriteback.enabled', 'Greenhouse write-back must be explicitly on or off.');
	}
	if (writeback && writeback.enabled === true) {
		if (isBlank(writeback.referralSourceId)) {
			push('greenhouseWriteback.referralSourceId', 'The referral source id is required while write-back is on.');
		}
		if (isBlank(writeback.selfSourceId)) {
			push('greenhouseWriteback.selfSourceId', 'The self source id is required while write-back is on.');
		}
		if (isBlank(writeback.onBehalfOfUserId)) {
			push('greenhouseWriteback.onBehalfOfUserId', 'The On-Behalf-Of user id is required while write-back is on.');
		}
	}

	const jobCreate = config.jobCreate;
	if (!jobCreate || typeof jobCreate.enabled !== 'boolean') {
		push('jobCreate.enabled', 'Job create must be explicitly on or off.');
	}
	if (jobCreate && (typeof jobCreate.defaults !== 'object' || jobCreate.defaults === null)) {
		push('jobCreate.defaults', 'Job-create defaults must be an object.');
	}
	if (jobCreate && jobCreate.bonus && !Array.isArray(jobCreate.bonus.rules)) {
		push('jobCreate.bonus', 'Bonus rules must be a list.');
	}
	if (jobCreate && jobCreate.defaults && Object.prototype.hasOwnProperty.call(jobCreate.defaults, 'type')) {
		push(
			'jobCreate.defaults.type',
			"Defaults must not contain 'type' — the erin-connect type is the POST sibling (erinConnect.type), not a job field."
		);
	}

	const limits = config.limits || {};
	for (const key of Object.keys(limits)) {
		const v = limits[key];
		if (v !== undefined && (typeof v !== 'number' || v < 0)) {
			push(`limits.${key}`, 'Must be a non-negative number.');
		}
	}

	return errors;
}

// Greenhouse ids are canonically NUMBERS in the seeded configs (source 200606, user 1732969, …) while
// anything non-numeric stays a string — the engine compares already-sourced ids loosely but sends
// write-back ids as-is, so the editor keeps the canonical shape: a plain integer becomes a number,
// everything else (including leading-zero strings) is left alone. Blank stays '' so isBlank catches it.
export function parseIdInput(value) {
	const s = String(value == null ? '' : value).trim();
	if (s === '') return '';
	return /^(0|[1-9]\d*)$/.test(s) ? Number(s) : s;
}

// Replace the identity's domain-equivalence groups, omitting the key entirely when none remain —
// the engine treats the key as optional, and `emailDomainEquivalences: []` would break a
// byte-faithful round-trip of a config that never had it.
export function setEmailDomainEquivalences(identity, groups) {
	const next = { ...(identity || {}) };
	if (groups && groups.length) next.emailDomainEquivalences = groups;
	else delete next.emailDomainEquivalences;
	return next;
}

function isNonEmptyString(v) {
	return typeof v === 'string' && v.trim() !== '';
}

function isBlank(v) {
	return v === undefined || v === null || v === '';
}
